/**
 * @file 基于原 OPM Demo 的 cytoscape 定制，注册 triangle-inner-* 箭头
 */
import type { Core } from 'cytoscape';

type Point = { x: number; y: number };

const OUTER_TRIANGLE_POINTS = [0, 0, 0.15, -0.3, -0.15, -0.3, 0, 0];
const INNER_TRIANGLE_POINTS = [0, -0.13, -0.05, -0.23, 0.05, -0.23, 0, -0.13];
const TRIANGLE_CIRCLE_POINTS = [0, -0.15, 0.15, -0.45, -0.15, -0.45, 0, -0.15];
// Influence：参考设计的双向箭头，使用内凹形状强调“空心”视觉
const INFLUENCE_ARROW_POINTS = [
  -0.033,
  -0.238,
  -0.091,
  -0.3,
  0,
  0,
  0.091,
  -0.3,
  0.033,
  -0.238,
  0,
  -0.203,
];

const transformPoint = (
  x: number,
  y: number,
  size: number,
  angle: number,
  translation: Point
): Point => {
  const xRotated = x * Math.cos(angle) - y * Math.sin(angle);
  const yRotated = x * Math.sin(angle) + y * Math.cos(angle);
  return {
    x: xRotated * size + translation.x,
    y: yRotated * size + translation.y,
  };
};

const transformPoints = (
  points: number[],
  size: number,
  angle: number,
  translation: Point
): Point[] => {
  const transformed: Point[] = [];
  for (let i = 0; i < points.length; i += 2) {
    transformed.push(
      transformPoint(points[i], points[i + 1], size, angle, translation)
    );
  }
  return transformed;
};

const drawInnerTriangle = (
  context: CanvasRenderingContext2D,
  outer: Point[],
  inner: Point[]
) => {
  if (context.beginPath) context.beginPath();
  const firstOuter = outer[0];
  context.moveTo(firstOuter.x, firstOuter.y);
  for (const pt of outer) context.lineTo(pt.x, pt.y);
  if (context.closePath) context.closePath();

  if (context.beginPath) context.beginPath();
  const [pt0, pt1, pt2] = inner;
  context.moveTo(inner[0].x + 0.023, inner[0].y - 0.05);
  for (let i = 0; i < 0.06; i += 0.01) {
    context.lineTo(pt0.x, pt0.y - i);
    context.lineTo(pt1.x + i, pt1.y + i);
    context.lineTo(pt2.x - i, pt2.y + i);
  }
  context.moveTo(inner[0].x, inner[0].y);
  for (const pt of inner) context.lineTo(pt.x, pt.y);
  if (context.closePath) context.closePath();
};

const drawTriangleInnerCircle = (
  context: CanvasRenderingContext2D,
  trianglePoints: Point[],
  rx: number,
  ry: number,
  r: number
) => {
  if (context.beginPath) context.beginPath();
  for (let i = r; i > 0; i -= 0.01) {
    context.arc(rx, ry - 0.2, r - i, 0, Math.PI * 2, false);
  }
  context.lineTo(rx - 0.01, ry - 0.2);
  const first = trianglePoints[0];
  context.moveTo(first.x, first.y + 0.15);
  for (const pt of trianglePoints) context.lineTo(pt.x, pt.y + 0.15);
  if (context.closePath) context.closePath();
};

const cloneBaseShape = (base: Record<string, any>) => ({
  collide: base.collide?.bind(base),
  roughCollide: base.roughCollide?.bind(base),
  spacing: base.spacing?.bind(base),
  gap: base.gap?.bind(base),
});

const registerCustomArrowShapes = (cy: Core) => {
  const renderer = (cy as any).renderer?.();
  if (!renderer?.arrowShapes) return;

  const { arrowShapes } = renderer;
  if (
    arrowShapes['triangle-inner-triangle'] &&
    arrowShapes['triangle-inner-circle'] &&
    arrowShapes['influence-arrow']
  )
    return;

  const baseTriangle = arrowShapes['triangle'];

  if (!arrowShapes['triangle-inner-triangle']) {
    arrowShapes['triangle-inner-triangle'] = {
      ...cloneBaseShape(baseTriangle),
      name: 'triangle-inner-triangle',
      points: OUTER_TRIANGLE_POINTS,
      innerPoints: INNER_TRIANGLE_POINTS,
      draw(
        context: CanvasRenderingContext2D,
        size: number,
        angle: number,
        translation: Point
      ) {
        const outerPts = transformPoints(this.points, size, angle, translation);
        const innerPts = transformPoints(
          this.innerPoints,
          size,
          angle,
          translation
        );
        drawInnerTriangle(context, outerPts, innerPts);
      },
    };
  }

  if (!arrowShapes['triangle-inner-circle']) {
    arrowShapes['triangle-inner-circle'] = {
      ...cloneBaseShape(baseTriangle),
      name: 'triangle-inner-circle',
      radius: 0.06,
      pointsTr: TRIANGLE_CIRCLE_POINTS,
      draw(
        context: CanvasRenderingContext2D,
        size: number,
        angle: number,
        translation: Point
      ) {
        const triPts = transformPoints(this.pointsTr, size, angle, translation);
        drawTriangleInnerCircle(
          context,
          triPts,
          translation.x,
          translation.y,
          this.radius * size
        );
      },
      spacing(edge: any) {
        if (renderer.getArrowWidth) {
          return (
            renderer.getArrowWidth(
              edge.pstyle('width').pfValue,
              edge.pstyle('arrow-scale').value
            ) * this.radius
          );
        }
        return baseTriangle.spacing?.call(baseTriangle, edge) ?? 0;
      },
    };
  }

  if (!arrowShapes['influence-arrow']) {
    arrowShapes['influence-arrow'] = {
      ...cloneBaseShape(baseTriangle),
      name: 'influence-arrow',
      points: INFLUENCE_ARROW_POINTS,
      draw(
        context: CanvasRenderingContext2D,
        size: number,
        angle: number,
        translation: Point
      ) {
        // 使用自定义 points 绘制 Influence 箭头
        const pts = transformPoints(this.points, size, angle, translation);
        renderer.arrowShapeImpl('polygon')(context, pts);
      },
    };
  }

  cy.emit('customArrowShapesRegistered');
};

export default registerCustomArrowShapes;
